import { useMemo } from 'react'

import { ChartColumnStacked } from '@gravity-ui/icons'
import { differenceInCalendarDays, eachDayOfInterval, eachMonthOfInterval, format, parseISO } from 'date-fns'
import * as Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/ui/EmptyState'
import { useThemeStore } from '@/store/theme.store'
import { useVehicleAnalyticsStore } from '@/store/vehicleAnalytics.store'
import { COST_CATEGORIES } from '@/types/vehicleAnalytics'

import { CATEGORY_COLORS, chartTheme } from './chartTheme'

// Ranges up to this length are shown by day, longer ones by month
const MAX_DAILY_RANGE = 45

export function CostTimelineChart() {
  const { t } = useTranslation()
  const { entries, filters } = useVehicleAnalyticsStore()
  const isDark = useThemeStore(s => s.dark)

  const options = useMemo<Highcharts.Options>(() => {
    if (!filters.from || !filters.to || entries.length === 0) return {}

    const start = parseISO(filters.from)
    const end = parseISO(filters.to)
    const daily = differenceInCalendarDays(end, start) <= MAX_DAILY_RANGE
    const keyOf = (date: string) => (daily ? date.slice(0, 10) : date.slice(0, 7))

    const buckets = (daily ? eachDayOfInterval({ start, end }) : eachMonthOfInterval({ start, end }))
      .map(d => format(d, daily ? 'yyyy-MM-dd' : 'yyyy-MM'))
    const index = new Map(buckets.map((key, i) => [key, i]))

    const values = Object.fromEntries(COST_CATEGORIES.map(c => [c, buckets.map(() => 0)])) as Record<string, number[]>
    entries.forEach(entry => {
      const i = index.get(keyOf(entry.date))
      if (i !== undefined) values[entry.category][i] += entry.amountPLN
    })

    const categories = buckets.map(key => format(parseISO(daily ? key : `${key}-01`), daily ? 'dd.MM' : 'MM.yyyy'))
    const { textColor, lineColor, gridLineColor, base } = chartTheme(isDark)

    return {
      ...base,
      chart: { ...base.chart, type: 'column' },
      xAxis: {
        categories,
        labels: { style: { color: textColor } },
        lineColor,
        tickColor: lineColor,
      },
      yAxis: {
        title: { text: 'PLN', style: { color: textColor } },
        labels: { style: { color: textColor } },
        gridLineColor,
        stackLabels: { enabled: false },
      },
      tooltip: {
        shared: true,
        valueDecimals: 2,
        valueSuffix: ' PLN',
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          borderRadius: 3,
        },
      },
      series: COST_CATEGORIES.map(category => ({
        type: 'column',
        name: t(`vehicle_analytics.categories.${category}`),
        color: CATEGORY_COLORS[category],
        data: values[category].map(v => Number(v.toFixed(2))),
      })),
    }
  }, [entries, filters, isDark, t])

  if (entries.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <EmptyState
          icon={<ChartColumnStacked className="size-8" />}
          title={t('analytics.no_expenses_period')}
          description={''}
        />
      </div>
    )
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', minHeight: '320px' } }}
    />
  )
}
